import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { ContactDto } from '../types/Contact';
import { CONTACT_TYPES } from '../constants/contact';
import { FormSelect } from '../controls/FormSelect';
import CustomInput from '../controls/CustomInput';

const ContactFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<ContactDto>({
    id: 0,
    contactType: '',
    companyWebsite: '',
    email: '',
    phoneNumber: '',
    contactNotes: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadContact = async (contactId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/contact/getcontact?id=${contactId}`);
        setFormData(response.data.contact);
      } catch (error) {
        console.error('Error loading Contact:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const contactId = new URLSearchParams(location.search).get('contactId');
    if (contactId) {
      setIsEditing(true);
      loadContact(parseInt(contactId));
    }
  }, [location.search, api]);

  const handleFieldChange = (field: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      if (isEditing) {
        await api.put(`/api/contact/updatecontact`, formData);
      } else {
        await api.post(`/api/contact/createcontact?familyId=${familyId}`, formData);
      }
      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Contact' : 'Add New Contact'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update Contact information' : 'Enter Contact information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormSelect
                  label="Contact Type"
                  value={formData.contactType || null}
                  options={Object.entries(CONTACT_TYPES).map(([key, value]) => ({ value: key, label: value as string }))}
                  onChange={(val) => setFormData(prev => ({ ...prev, contactType: (val as string) ?? '' }))}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Company Website"
                  name="companyWebsite"
                  value={formData.companyWebsite ?? ''}
                  onChange={handleFieldChange('companyWebsite')}
                />
              </div>
              <div>
                <CustomInput
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFieldChange('email')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Phone Number"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleFieldChange('phoneNumber')}
                  required
                />
              </div>
              <div>
                <CustomInput
                  label="Contact Notes"
                  name="contactNotes"
                  value={formData.contactNotes ?? ''}
                  onChange={handleFieldChange('contactNotes')}
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Contact' : 'Add Contact')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactFormPage;
